import React, { useId, useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography, alpha, useTheme } from '@mui/material';
import { ShowChart, BarChart as BarChartIcon, AreaChart as AreaChartIcon, TrackChanges } from '@mui/icons-material';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Line, LineChart, PolarAngleAxis,
  PolarGrid, PolarRadiusAxis, Radar, RadarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { GradeScale } from './useGradeScale';
import { mean, OVERALL_ID, pct, TrendRow, TrendSeries } from './trend';

export type ChartType = 'line' | 'area' | 'bar' | 'radar';

const CHART_TYPES: Array<{ value: ChartType; label: string; icon: React.ReactElement }> = [
  { value: 'line', label: 'Lines', icon: <ShowChart sx={{ fontSize: 18 }} /> },
  { value: 'area', label: 'Areas', icon: <AreaChartIcon sx={{ fontSize: 18 }} /> },
  { value: 'bar', label: 'Bars', icon: <BarChartIcon sx={{ fontSize: 18 }} /> },
  { value: 'radar', label: 'Subject balance', icon: <TrackChanges sx={{ fontSize: 18 }} /> },
];

export interface Choice<T extends string> { value: T; label: string; icon?: React.ReactElement }

/**
 * A compact segmented switch. Options with an icon show the icon with the
 * label as a tooltip; the rest show their label as text.
 */
export function ChoiceToggle<T extends string>({ options, value, onChange }: {
  options: Array<Choice<T>>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <ToggleButtonGroup value={value} exclusive size="small" onChange={(_, v) => v && onChange(v)}>
      {options.map((o) => (
        <MuiTooltip key={o.value} title={o.icon ? o.label : ''}>
          <ToggleButton value={o.value} aria-label={o.label}
            sx={{ px: o.icon ? 1.25 : 1.5, textTransform: 'none', fontWeight: 600, fontSize: 13, lineHeight: 1.5 }}>
            {o.icon ?? o.label}
          </ToggleButton>
        </MuiTooltip>
      ))}
    </ToggleButtonGroup>
  );
}

export const ChartTypeToggle: React.FC<{ value: ChartType; onChange: (v: ChartType) => void }> = ({ value, onChange }) => (
  <ChoiceToggle options={CHART_TYPES} value={value} onChange={onChange} />
);

/** Recharts focuses the chart on click; the browser ring around it reads as a selection box. */
const ChartFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ '& .recharts-wrapper, & .recharts-surface': { outline: 'none' } }}>{children}</Box>
);

/** Axis, grid and tooltip styling shared by every analysis chart. */
export const useChartStyle = () => {
  const theme = useTheme();
  return useMemo(() => ({
    tick: { fontSize: 12, fill: theme.palette.text.secondary },
    grid: alpha(theme.palette.divider, 0.6),
    axisLine: { stroke: theme.palette.divider },
    cursor: { fill: alpha(theme.palette.text.primary, 0.04) },
  }), [theme]);
};

/** The floating card every analysis tooltip renders in. */
export const TooltipCard: React.FC<{ title: React.ReactNode; accent?: string; children: React.ReactNode }> = ({ title, accent, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      bgcolor: 'background.paper', px: 1.75, py: 1.25, borderRadius: 2, minWidth: 180,
      border: `1px solid ${theme.palette.divider}`, borderTop: accent ? `3px solid ${accent}` : undefined,
      boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(15,23,42,0.12)',
    }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>{title}</Typography>
      {children}
    </Box>
  );
};

const Swatch: React.FC<{ color: string; hollow?: boolean }> = ({ color, hollow }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: hollow ? 'transparent' : color, border: hollow ? `1px dashed ${color}` : 'none' }} />
);

/** One row of a tooltip: swatch, name, value. */
export const TooltipRow: React.FC<{ color: string; name: string; value: React.ReactNode; strong?: boolean; hollow?: boolean }> = ({ color, name, value, strong, hollow }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
    <Swatch color={color} hollow={hollow} />
    <Typography variant="body2" sx={{ flex: 1, fontWeight: strong ? 700 : 400 }} noWrap>{name}</Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

const TrendTooltip: React.FC<any> = ({ active, payload, series }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const row: TrendRow = payload[0].payload.row;
  const ordered = (series as TrendSeries[])
    .filter((s) => row.values[s.id] != null)
    .sort((a, b) => (b.overall ? 1 : 0) - (a.overall ? 1 : 0) || (row.values[b.id] as number) - (row.values[a.id] as number));
  return (
    <TooltipCard title={row.examName}>
      {ordered.map((s) => <TooltipRow key={s.id} color={s.color} name={s.name} value={pct(row.values[s.id])} strong={s.overall} />)}
      {row.absent.map((name) => <TooltipRow key={name} color={theme.palette.warning.main} name={name} value="Absent" hollow />)}
    </TooltipCard>
  );
};

/**
 * Scores across exams, one line per subject with the overall line drawn
 * heaviest. With a single exam there is no trend to draw, so subjects are
 * ranked as bars instead.
 */
export const TrendChart: React.FC<{
  rows: TrendRow[];
  series: TrendSeries[];
  type: ChartType;
  grades: GradeScale;
}> = ({ rows, series, type, grades }) => {
  const style = useChartStyle();
  const gid = useId().replace(/:/g, '');
  const data = useMemo(() => rows.map((row) => ({ name: row.examName, row, ...row.values })), [rows]);

  if (rows.length === 1) {
    const row = rows[0];
    const items = series
      .filter((s) => !s.overall && row.values[s.id] != null)
      .map((s) => ({ id: s.id, name: s.name, value: row.values[s.id] as number }));
    return <ScoreBars items={items} average={row.values[OVERALL_ID] ?? null} averageLabel="Overall" grades={grades} />;
  }

  if (type === 'radar') {
    const radar = series.filter((s) => !s.overall).map((s) => ({ subject: s.name, value: mean(rows.map((r) => r.values[s.id])) ?? 0 }));
    const color = series.find((s) => s.overall)?.color ?? series[0]?.color;
    return (
      <ChartFrame><ResponsiveContainer width="100%" height={380}>
        <RadarChart data={radar} outerRadius="72%">
          <PolarGrid stroke={style.grid} />
          <PolarAngleAxis dataKey="subject" tick={style.tick} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ ...style.tick, fontSize: 10 }} axisLine={false} />
          <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.18} strokeWidth={2} />
          <Tooltip content={({ active, payload }: any) => active && payload?.length ? (
            <TooltipCard title={payload[0].payload.subject}>
              <TooltipRow color={color} name="Average" value={pct(payload[0].payload.value)} strong />
            </TooltipCard>
          ) : null} />
        </RadarChart>
      </ResponsiveContainer></ChartFrame>
    );
  }

  const xAxis = (
    <XAxis dataKey="name" tick={style.tick} padding={{ left: 28, right: 28 }} axisLine={style.axisLine} tickLine={false}
      interval={0} angle={rows.length > 6 ? -20 : 0} textAnchor={rows.length > 6 ? 'end' : 'middle'} height={rows.length > 6 ? 60 : 32} />
  );
  // Scores bunch near the top, so lines start the axis just under the lowest one.
  // Bars keep a zero baseline; a cut-off bar would exaggerate the gaps.
  const lowest = Math.min(100, ...rows.flatMap((r) => series.map((s) => r.values[s.id] ?? 100)));
  const reach = type === 'bar' ? 0 : Math.max(0, lowest - 5);
  const step = 100 - reach > 50 ? 20 : 10;
  const floor = Math.floor(reach / step) * step;
  const ticks = Array.from({ length: (100 - floor) / step + 1 }, (_, i) => floor + i * step);
  const yAxis = (
    <YAxis domain={[ticks[0], 100]} ticks={ticks} tick={style.tick} axisLine={false} tickLine={false} width={40} unit="%" />
  );
  const grid = <CartesianGrid vertical={false} stroke={style.grid} strokeDasharray="3 3" />;
  const tooltip = <Tooltip cursor={type === 'bar' ? style.cursor : { stroke: style.grid }} content={<TrendTooltip series={series} />} />;
  const margin = { top: 16, right: 16, left: 0, bottom: 0 };

  if (type === 'bar') {
    return (
      <ChartFrame><ResponsiveContainer width="100%" height={380}>
        <BarChart data={data} margin={margin} barGap={2}>
          {grid}{xAxis}{yAxis}{tooltip}
          {series.map((s) => (
            <Bar key={s.id} dataKey={s.id} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={28}>
              {series.length <= 3 && (
                <LabelList dataKey={s.id} position="top" formatter={(v: any) => (v == null ? '' : `${Math.round(Number(v))}%`)}
                  style={{ fontSize: 11, fontWeight: 600, fill: style.tick.fill }} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer></ChartFrame>
    );
  }

  if (type === 'area') {
    return (
      <ChartFrame><ResponsiveContainer width="100%" height={380}>
        <AreaChart data={data} margin={margin}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.id} id={`${gid}-${s.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={s.overall ? 0.18 : 0.28} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          {grid}{xAxis}{yAxis}{tooltip}
          {series.map((s) => (
            <Area key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={s.color} fill={`url(#${gid}-${s.id})`}
              strokeWidth={s.overall ? 3 : 1.75} connectNulls={false} dot={false} activeDot={{ r: s.overall ? 5 : 4 }} />
          ))}
        </AreaChart>
      </ResponsiveContainer></ChartFrame>
    );
  }

  return (
    <ChartFrame><ResponsiveContainer width="100%" height={380}>
      <LineChart data={data} margin={margin}>
        {grid}{xAxis}{yAxis}{tooltip}
        {series.map((s) => (
          <Line key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={s.color}
            strokeWidth={s.overall ? 3.25 : 1.75} strokeOpacity={s.overall ? 1 : 0.85}
            dot={{ r: s.overall ? 4.5 : 3, strokeWidth: 0, fill: s.color }} activeDot={{ r: s.overall ? 6 : 5 }} connectNulls={false} />
        ))}
      </LineChart>
    </ResponsiveContainer></ChartFrame>
  );
};

export interface ScoreItem {
  id: string;
  name: string;
  value: number;
  detail?: string;
  /** An extra tooltip line, e.g. a pass/fail result. */
  note?: { text: string; color: string };
  /** Outlined in red, e.g. a failed result. */
  flagged?: boolean;
}

const shorten = (name: string, max: number) => (name.length > max ? `${name.slice(0, max - 1)}…` : name);

/** A column label set on two lines (first name, then the rest) so neighbouring names never run together. */
const TwoLineTick: React.FC<any> = ({ x, y, payload, nameOf, color }) => {
  const [first, ...rest] = String(nameOf(payload.value)).split(' ');
  return (
    <text x={x} y={y + 14} textAnchor="middle" fontSize={12} fill={color}>
      <tspan x={x}>{shorten(first, 13)}</tspan>
      {rest.length > 0 && <tspan x={x} dy={15} opacity={0.7}>{shorten(rest.join(' '), 13)}</tspan>}
    </text>
  );
};

/**
 * Subjects or students as bars in their grade colours, the average marked.
 * Rows (horizontal bars) keep every name whole; columns read like a classic
 * bar chart. Bars are ranked highest first unless `keepOrder` is set, e.g.
 * for roll-number order. With `onSelect` each bar opens that row.
 */
export const ScoreBars: React.FC<{
  items: ScoreItem[];
  average: number | null;
  averageLabel: string;
  grades: GradeScale;
  columns?: boolean;
  keepOrder?: boolean;
  rowHeight?: number;
  onSelect?: (item: ScoreItem) => void;
  selectHint?: string;
}> = ({ items, average, averageLabel, grades, columns, keepOrder, rowHeight = 42, onSelect, selectHint }) => {
  const style = useChartStyle();
  const theme = useTheme();
  const data = useMemo(() => (keepOrder ? items : [...items].sort((a, b) => b.value - a.value)), [items, keepOrder]);
  const nameOf = (id: string) => data.find((d) => d.id === id)?.name ?? '';
  const crowded = data.length > 14;

  const tooltip = (
    <Tooltip cursor={style.cursor} content={({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      const d: ScoreItem = payload[0].payload;
      const color = grades.color(d.value);
      return (
        <TooltipCard title={d.name} accent={color}>
          {d.detail && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{d.detail}</Typography>}
          <TooltipRow color={color} name={`Grade ${grades.letter(d.value)}`} value={pct(d.value)} strong />
          {average != null && <TooltipRow color={theme.palette.text.secondary} name={averageLabel} value={pct(average)} hollow />}
          {d.note && <Typography variant="caption" fontWeight={700} display="block" sx={{ mt: 0.75, color: d.note.color }}>{d.note.text}</Typography>}
          {onSelect && selectHint && <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.75 }}>{selectHint}</Typography>}
        </TooltipCard>
      );
    }} />
  );
  const averageLine = average != null && (
    <ReferenceLine {...(columns ? { y: average } : { x: average })} stroke={theme.palette.text.secondary} strokeDasharray="4 4"
      label={{ value: `${averageLabel} ${pct(average, 0)}`, position: columns ? 'insideTopRight' : 'top', fill: theme.palette.text.secondary, fontSize: 11 }} />
  );
  const bars = (
    <Bar dataKey="value" radius={columns ? [6, 6, 0, 0] : [0, 6, 6, 0]} maxBarSize={columns ? 44 : 22}
      cursor={onSelect ? 'pointer' : undefined} onClick={onSelect ? (d: any) => onSelect(d.payload ?? d) : undefined}
      animationDuration={500}>
      {data.map((d) => (
        <Cell key={d.id} fill={grades.color(d.value)}
          stroke={d.flagged ? theme.palette.error.main : undefined} strokeWidth={d.flagged ? 2 : 0} strokeDasharray={d.flagged ? '4 2' : undefined} />
      ))}
      <LabelList dataKey="value" position={columns ? 'top' : 'right'}
        formatter={(v: any) => (columns && crowded ? `${Math.round(Number(v))}%` : pct(Number(v)))}
        style={{ fontSize: columns && crowded ? 10 : 12, fontWeight: 600, fill: theme.palette.text.primary, stroke: 'none' }} />
    </Bar>
  );
  const percentAxis = { type: 'number' as const, domain: [0, 100], ticks: [0, 25, 50, 75, 100], unit: '%', tick: style.tick, axisLine: false, tickLine: false };

  if (columns) {
    return (
      <ChartFrame><ResponsiveContainer width="100%" height={crowded ? 440 : 400}>
        <BarChart data={data} margin={{ top: 24, right: 16, left: 0, bottom: 0 }} barCategoryGap="22%">
          <CartesianGrid vertical={false} stroke={style.grid} strokeDasharray="3 3" />
          <XAxis dataKey="id" interval={0} axisLine={style.axisLine} tickLine={false}
            {...(crowded
              ? { tick: { ...style.tick, fill: theme.palette.text.primary }, angle: -40, textAnchor: 'end', height: 90, tickFormatter: (id: string) => shorten(nameOf(id), 16) }
              : { tick: <TwoLineTick nameOf={nameOf} color={theme.palette.text.primary} />, height: 44 })} />
          <YAxis {...percentAxis} width={44} />
          {tooltip}{averageLine}{bars}
        </BarChart>
      </ResponsiveContainer></ChartFrame>
    );
  }

  return (
    <ChartFrame><ResponsiveContainer width="100%" height={Math.max(220, data.length * rowHeight + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 20, right: 56, left: 8, bottom: 0 }} barCategoryGap="28%">
        <CartesianGrid horizontal={false} stroke={style.grid} strokeDasharray="3 3" />
        <XAxis {...percentAxis} />
        <YAxis type="category" dataKey="id" width={150} axisLine={false} tickLine={false}
          tick={{ ...style.tick, fill: theme.palette.text.primary }} tickFormatter={nameOf} />
        {tooltip}{averageLine}{bars}
      </BarChart>
    </ResponsiveContainer></ChartFrame>
  );
};
